import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Users, Flag, LogOut, Link2, Package } from 'lucide-react';
import { Link } from 'react-router-dom';

interface SidebarProps {
  className?: string;
  onLogout: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  requiredRole?: 'admin' | 'user';
  badge?: string;
}

const navigationItems: NavigationItem[] = [
  //   {
  //     name: 'Dashboard',
  //     href: '/admin/dashboard',
  //     icon: LayoutDashboard,
  //   },
  {
    name: 'Users',
    href: '/admin',
    icon: Users,
  },
  {
    name: 'Products',
    href: '/admin/products',
    icon: Package,
  },
  {
    name: 'Flagged Products',
    href: '/admin/flagged-products',
    icon: Flag,
  },
  {
    name: 'Link Products',
    href: '/admin/link-products',
    icon: Link2,
  },
];

export const Sidebar = ({ className, onLogout }: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // All navigation items are shown since sidebar is admin-only
  const filteredItems = navigationItems;

  const handleNavigation = (href: string) => {
    navigate(href);
  };

  return (
    <div
      className={cn(
        'flex fixed top-0 left-0 z-50 flex-col w-64 h-screen border-r bg-card border-border',
        'hidden lg:flex', // Hide on mobile, show on large screens
        className
      )}
    >
      {/* Logo/Brand */}
      <div className="flex items-center px-6 h-20 border-b border-border">
        <Link to="/" className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg">
            <img
              src="/assets/nabt-vector.svg"
              alt="NABT"
              className="object-contain w-full h-full"
            />
          </div>
          <div>
            <h1 className="text-lg font-bold text-primary">Nabt-Intel</h1>
            <p className="text-xs text-primary/80">Market Intelligence</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2">
        {filteredItems.map(item => {
          const isActive =
            (item.name === 'Dashboard' &&
              location.pathname === '/admin/dashboard') ||
            (item.name === 'Users' &&
              location.pathname.startsWith('/admin') &&
              location.pathname !== '/admin/flagged-products' &&
              location.pathname !== '/admin/dashboard' &&
              !location.pathname.startsWith('/admin/link-products') &&
              !location.pathname.startsWith('/admin/products')) ||
            (item.name === 'Products' &&
              location.pathname.startsWith('/admin/products')) ||
            (item.name === 'Flagged Products' &&
              location.pathname === '/admin/flagged-products') ||
            (item.name === 'Link Products' &&
              location.pathname.startsWith('/admin/link-products'));

          return (
            <button
              key={item.name}
              onClick={() => handleNavigation(item.href)}
              className={cn(
                'flex justify-between items-center px-3 py-2 w-full text-sm font-medium rounded-lg transition-colors',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <div className="flex items-center space-x-3">
                <item.icon
                  className={cn(
                    'w-5 h-5',
                    isActive
                      ? 'text-accent-foreground'
                      : 'text-muted-foreground'
                  )}
                />
                <span>{item.name}</span>
              </div>
              {item.badge && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User Info */}
      <div className="p-4 space-y-3 border-t border-border">
        <div className="flex items-center space-x-3">
          <div className="flex justify-center items-center w-8 h-8 rounded-full bg-muted">
            <span className="text-sm font-medium text-muted-foreground">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-foreground">
              {user?.name}
            </p>
            <p className="text-xs capitalize text-muted-foreground">
              {user?.role}
            </p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center px-3 py-2 space-x-2 w-full text-sm font-medium rounded-lg transition-colors text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};
